/**
 * 無限劍制特效系統 (簡化版)
 * C級特殊效果，劍雨具有指向性，落向被翻轉的棋子
 */

export interface Position {
  x: number;
  y: number;
}



/**
 * 簡化版陀飛輪（C級適用）
 */
export class SimplifiedTourbillon {
  public x: number;
  public y: number;
  private time = 0;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  update(deltaTime: number): void {
    this.time += deltaTime;
  }

  draw(ctx: CanvasRenderingContext2D, alpha: number): void {
    const minDimension = Math.min(window.innerWidth, window.innerHeight);
    const baseRadius = minDimension * 0.14; // 進一步增大齒輪尺寸
    const rotation = this.time * 0.3; // 較慢的旋轉速度

    ctx.save();
    ctx.translate(this.x, this.y - window.innerHeight * 0.02);
    ctx.globalAlpha = alpha * 0.5; // C級較低透明度

    // 外環（簡化版）
    this.drawSimplifiedRing(ctx, baseRadius, rotation);

    // 中心擺輪（簡化版）
    this.drawSimplifiedBalance(ctx, baseRadius, this.time);

    ctx.restore();
  }

  private drawSimplifiedRing(ctx: CanvasRenderingContext2D, radius: number, rotation: number): void {
    ctx.save();
    ctx.rotate(-rotation * 0.5);

    // 外層厚重環 - 增加厚度和重量感
    const outerGradient = ctx.createRadialGradient(0, 0, radius * 0.65, 0, 0, radius);
    outerGradient.addColorStop(0, 'rgba(240,240,240,0.8)');
    outerGradient.addColorStop(0.3, 'rgba(200,200,200,0.9)');
    outerGradient.addColorStop(0.7, 'rgba(160,160,160,0.8)');
    outerGradient.addColorStop(1, 'rgba(100,100,100,0.6)');

    ctx.fillStyle = outerGradient;
    ctx.beginPath();
    ctx.arc(0, 0, radius, 0, Math.PI * 2);
    ctx.arc(0, 0, radius * 0.65, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();

    // 中層環 - 增加層次感
    const midGradient = ctx.createRadialGradient(0, 0, radius * 0.55, 0, 0, radius * 0.75);
    midGradient.addColorStop(0, 'rgba(220,220,220,0.7)');
    midGradient.addColorStop(1, 'rgba(140,140,140,0.5)');

    ctx.fillStyle = midGradient;
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.75, 0, Math.PI * 2);
    ctx.arc(0, 0, radius * 0.55, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();

    // 外層大齒 - 厚重的齒輪結構
    ctx.strokeStyle = 'rgba(255,255,255,0.8)';
    ctx.lineWidth = 4;
    for (let i = 0; i < 24; i++) {
      const angle = (i * Math.PI * 2) / 24;
      ctx.beginPath();
      ctx.moveTo(Math.cos(angle) * radius * 0.65, Math.sin(angle) * radius * 0.65);
      ctx.lineTo(Math.cos(angle) * radius * 0.98, Math.sin(angle) * radius * 0.98);
      ctx.stroke();

      // 齒輪頂端加厚
      ctx.lineWidth = 6;
      ctx.strokeStyle = 'rgba(255,255,255,0.9)';
      ctx.beginPath();
      ctx.moveTo(Math.cos(angle) * radius * 0.92, Math.sin(angle) * radius * 0.92);
      ctx.lineTo(Math.cos(angle) * radius * 0.98, Math.sin(angle) * radius * 0.98);
      ctx.stroke();
      ctx.lineWidth = 4;
      ctx.strokeStyle = 'rgba(255,255,255,0.8)';
    }

    // 中層精密齒輪 - 更細緻的紋理
    ctx.strokeStyle = 'rgba(220,220,220,0.7)';
    ctx.lineWidth = 2;
    for (let i = 0; i < 48; i++) {
      const angle = (i * Math.PI * 2) / 48;
      ctx.beginPath();
      ctx.moveTo(Math.cos(angle) * radius * 0.55, Math.sin(angle) * radius * 0.55);
      ctx.lineTo(Math.cos(angle) * radius * 0.72, Math.sin(angle) * radius * 0.72);
      ctx.stroke();

      // 每8個齒輪添加強化線
      if (i % 8 === 0) {
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(240,240,240,0.8)';
        ctx.beginPath();
        ctx.moveTo(Math.cos(angle) * radius * 0.52, Math.sin(angle) * radius * 0.52);
        ctx.lineTo(Math.cos(angle) * radius * 0.75, Math.sin(angle) * radius * 0.75);
        ctx.stroke();
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(220,220,220,0.7)';
      }
    }

    // 內層複雜機械結構
    for (let ring = 0; ring < 3; ring++) {
      const ringRadius = radius * (0.45 - ring * 0.08);
      const teethCount = 36 - ring * 8;

      ctx.strokeStyle = `rgba(200,200,200,${0.6 - ring * 0.1})`;
      ctx.lineWidth = 2 - ring * 0.3;

      for (let i = 0; i < teethCount; i++) {
        const angle = (i * Math.PI * 2) / teethCount;
        ctx.beginPath();
        ctx.moveTo(Math.cos(angle) * ringRadius * 0.85, Math.sin(angle) * ringRadius * 0.85);
        ctx.lineTo(Math.cos(angle) * ringRadius * 1.1, Math.sin(angle) * ringRadius * 1.1);
        ctx.stroke();
      }
    }

    ctx.restore();
  }

  private drawSimplifiedBalance(ctx: CanvasRenderingContext2D, radius: number, time: number): void {
    ctx.save();
    ctx.rotate(Math.sin(time * 4) * 0.2); // 擺輪搖擺

    // 外層擺輪環 - 更精緻的漸層
    const outerBalanceGradient = ctx.createRadialGradient(0, 0, radius * 0.12, 0, 0, radius * 0.32);
    outerBalanceGradient.addColorStop(0, 'rgba(255,255,255,0.9)');
    outerBalanceGradient.addColorStop(0.3, 'rgba(240,240,240,0.8)');
    outerBalanceGradient.addColorStop(0.7, 'rgba(180,180,180,0.7)');
    outerBalanceGradient.addColorStop(1, 'rgba(120,120,120,0.5)');

    ctx.fillStyle = outerBalanceGradient;
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.32, 0, Math.PI * 2);
    ctx.arc(0, 0, radius * 0.12, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();

    // 內層擺輪環 - 金屬質感
    const innerBalanceGradient = ctx.createRadialGradient(0, 0, radius * 0.08, 0, 0, radius * 0.18);
    innerBalanceGradient.addColorStop(0, 'rgba(255,255,255,1.0)');
    innerBalanceGradient.addColorStop(0.5, 'rgba(200,200,200,0.9)');
    innerBalanceGradient.addColorStop(1, 'rgba(150,150,150,0.7)');

    ctx.fillStyle = innerBalanceGradient;
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.18, 0, Math.PI * 2);
    ctx.arc(0, 0, radius * 0.08, 0, Math.PI * 2, true);
    ctx.closePath();
    ctx.fill();

    // 擺輪輻條 - 精細的機械結構
    ctx.strokeStyle = 'rgba(255,255,255,0.8)';
    ctx.lineWidth = 2;
    for (let i = 0; i < 12; i++) {
      const angle = (i * Math.PI * 2) / 12;
      ctx.beginPath();
      ctx.moveTo(Math.cos(angle) * radius * 0.08, Math.sin(angle) * radius * 0.08);
      ctx.lineTo(Math.cos(angle) * radius * 0.30, Math.sin(angle) * radius * 0.30);
      ctx.stroke();

      // 主輻條加強
      if (i % 3 === 0) {
        ctx.lineWidth = 3;
        ctx.strokeStyle = 'rgba(255,255,255,0.95)';
        ctx.beginPath();
        ctx.moveTo(Math.cos(angle) * radius * 0.08, Math.sin(angle) * radius * 0.08);
        ctx.lineTo(Math.cos(angle) * radius * 0.32, Math.sin(angle) * radius * 0.32);
        ctx.stroke();
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(255,255,255,0.8)';
      }
    }

    // 擺輪配重塊
    for (let i = 0; i < 4; i++) {
      const angle = (i * Math.PI * 2) / 4;
      const weightX = Math.cos(angle) * radius * 0.28;
      const weightY = Math.sin(angle) * radius * 0.28;

      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.beginPath();
      ctx.arc(weightX, weightY, radius * 0.03, 0, Math.PI * 2);
      ctx.fill();

      ctx.strokeStyle = 'rgba(200,200,200,0.8)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // 中心軸 - 更精緻的中心點
    const centerGradient = ctx.createRadialGradient(0, 0, 0, 0, 0, radius * 0.05);
    centerGradient.addColorStop(0, 'rgba(255,255,255,1.0)');
    centerGradient.addColorStop(0.7, 'rgba(220,220,220,0.9)');
    centerGradient.addColorStop(1, 'rgba(180,180,180,0.7)');

    ctx.fillStyle = centerGradient;
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.05, 0, Math.PI * 2);
    ctx.fill();

    // 中心點反光
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.beginPath();
    ctx.arc(-radius * 0.01, -radius * 0.01, radius * 0.02, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }
}

/**
 * 無限劍制主效果（C級版本）- 純齒輪特效
 */
export class UnlimitedBladeWorksEffect {
  public x: number;
  public y: number;
  public dead = false;
  private time = 0;
  private duration = 2.0; // 延長齒輪顯示時間
  private tourbillon: SimplifiedTourbillon;

  constructor(x: number, y: number, targetPositions: Position[] = []) {
    this.x = x;
    this.y = y;
    this.tourbillon = new SimplifiedTourbillon(x, y);
  }

  update(deltaTime: number): void {
    this.time += deltaTime;

    if (this.time > this.duration) {
      this.dead = true;
      return;
    }

    // 更新陀飛輪
    this.tourbillon.update(deltaTime);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.dead) {return;}

    // 計算淡入淡出
    let alpha = 1;
    if (this.time < 0.3) {
      alpha = this.time / 0.3; // 較慢淡入
    } else if (this.time > this.duration - 0.4) {
      alpha = (this.duration - this.time) / 0.4; // 較慢淡出
    }

    // 只繪製陀飛輪
    this.tourbillon.draw(ctx, alpha);
  }

}